import { Text } from '@cinch-labs/design-system'
import { useResponsiveEnvironment } from '@retail-common/util-hooks'
import styles from './finance-representative-example.module.css'
import { currencyValueFormatter } from '@cinch-labs/shared-util'
import { generateFinanceRepresentativeExampleContent } from './finance-representative-example-content-generator'

const formatCurrency = (value: number) =>
  currencyValueFormatter(value, false, { includePence: true })

interface FinanceFigures {
  annualMileageLimit: number
  creditAmount: number
  customerDeposit?: number
  excessMileageChargeInPence: number
  financeDepositContribution?: number
  fixedAnnualInterestRate: number
  monthlyPaymentsAmount: number
  numberOfMonthlyPayments: number
  onTheRoadPrice: number
  optionalFinalPayment: number
  optionToPurchaseFee?: number
  representativeApr: number
  retailerDiscount?: number
  testDriveVoucher?: number
  totalAmountPayable: number
  totalInterestAmount: number
}

export interface FinanceRepresentativeExampleProps extends FinanceFigures {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  minScreenWidthForTable?: number
}

export const FinanceRepresentativeExample = ({
  headingLevel = 'h3',
  minScreenWidthForTable = 0,
  annualMileageLimit,
  creditAmount,
  customerDeposit,
  excessMileageChargeInPence,
  financeDepositContribution,
  fixedAnnualInterestRate,
  monthlyPaymentsAmount,
  numberOfMonthlyPayments,
  onTheRoadPrice,
  optionalFinalPayment,
  optionToPurchaseFee,
  representativeApr,
  retailerDiscount,
  testDriveVoucher,
  totalAmountPayable,
  totalInterestAmount,
}: FinanceRepresentativeExampleProps) => {
  const { isDesktop } = useResponsiveEnvironment()

  const displayTable = isDesktop && window.innerWidth > minScreenWidthForTable

  const formattedFigures = {
    annualMileageLimit: `${new Intl.NumberFormat().format(annualMileageLimit)}`,
    creditAmount: formatCurrency(creditAmount),
    customerDeposit: customerDeposit
      ? formatCurrency(customerDeposit)
      : undefined,
    excessMileageChargeInPence,
    financeDepositContribution: financeDepositContribution
      ? formatCurrency(financeDepositContribution)
      : undefined,
    fixedAnnualInterestRate: `${fixedAnnualInterestRate}%`,
    monthlyPaymentsAmount: formatCurrency(monthlyPaymentsAmount),
    numberOfMonthlyPayments,
    onTheRoadPrice: formatCurrency(onTheRoadPrice),
    optionalFinalPayment: formatCurrency(optionalFinalPayment),
    optionToPurchaseFee: optionToPurchaseFee
      ? formatCurrency(optionToPurchaseFee)
      : undefined,
    representativeApr: `${representativeApr}%`,
    retailerDiscount: retailerDiscount
      ? formatCurrency(retailerDiscount)
      : undefined,
    testDriveVoucher: testDriveVoucher
      ? formatCurrency(testDriveVoucher)
      : undefined,
    totalAmountPayable: formatCurrency(totalAmountPayable),
    totalInterestAmount: formatCurrency(totalInterestAmount),
  }

  const { text, tableData } = generateFinanceRepresentativeExampleContent({
    ...formattedFigures,
    displayTable,
  })

  return (
    <section className={styles.container}>
      <Text
        element={headingLevel}
        fontWeight="bold"
        fontSize="s"
        className={styles.title}
      >
        Personal Contract Purchase (PCP) representative example
      </Text>

      <Text fontWeight="book" fontSizeFixed="xs">
        {text}
      </Text>

      {displayTable && (
        <table className={styles.table}>
          <tbody>
            <tr className={styles.row}>
              {tableData.map((item: { term: string; details: string }) => (
                <td key={item.term}>
                  <Text
                    fontSizeFixed="xs"
                    fontWeight="book"
                    className={styles.columnHeader}
                  >
                    {item.term}
                  </Text>
                  <Text fontSizeFixed="xs" fontWeight="bold">
                    {item.details}
                  </Text>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </section>
  )
}
